import { usePosthogIdentityReady } from "~/context/posthog";
import { createContext, useCallback, useContext, useEffect, useReducer, useRef, type ReactNode } from "react";
import { trackParticipation } from "~/utils/analytics";
import { ParticipationAnalytics, type ParticipationProperties } from "./analytics";
import { adjacentParticipationStep, participationReducer, resolveParticipationStep, type FlowPolicy, type NavigationReason, type ParticipationOperation, type ParticipationOutcome, type ParticipationState, type ParticipationStep } from "./state";

export type ParticipationFlow = {
  step: ParticipationStep | null;
  pending: ParticipationState["pending"];
  go: (step: ParticipationStep, reason?: NavigationReason) => void;
  back: () => void;
  next: () => void;
  close: () => void;
  run: <T>(operation: ParticipationOperation, work: () => Promise<T>) => Promise<T | undefined>;
  observe: (operation: ParticipationOperation) => ReturnType<ParticipationAnalytics["start"]> | undefined;
  complete: (outcome: ParticipationOutcome) => void;
};
const Context = createContext<ParticipationFlow | null>(null);
export const useOptionalParticipationFlow = () => useContext(Context);
export function useParticipationFlow() {
  const flow = useOptionalParticipationFlow();
  if (!flow) throw new Error("ParticipationFlowProvider is required");
  return flow;
}

type Props = {
  scope: string;
  requestedStep: string | null;
  policy: FlowPolicy;
  facts: Omit<ParticipationProperties, "schema_version" | "flow_id" | "step">;
  onNavigate: (step: ParticipationStep, replace?: boolean) => void;
  onClose: () => void;
  persistAnalytics?: boolean;
  children: ReactNode;
};

export function useParticipationController(props: Omit<Props, "children">): ParticipationFlow {
  const identityReady = usePosthogIdentityReady();
  const [state, dispatch] = useReducer(participationReducer, { scope: props.scope, pending: null, navigationReason: "entry" });
  const current = useRef(props);
  current.current = props;
  const stateRef = useRef(state);
  if (stateRef.current.scope !== props.scope) stateRef.current = participationReducer(stateRef.current, { type: "scope", scope: props.scope });
  const journal = useRef<{ scope: string; eventId: string; analytics: ParticipationAnalytics } | null>(null);
  const queuedActions = useRef<{ journal: ParticipationAnalytics; finish: () => void }[]>([]);
  const queuedOutcomes = useRef<{ scope: string; values: ParticipationOutcome[] }>({ scope: props.scope, values: [] });
  const readyRef = useRef(identityReady);
  readyRef.current = identityReady;
  const step = resolveParticipationStep(props.requestedStep, props.policy);
  const stepRef = useRef(step);
  stepRef.current = step;
  const pending = state.scope === props.scope ? state.pending : null;

  useEffect(() => {
    const history = () => {
      stateRef.current = { ...stateRef.current, navigationReason: "history" };
      dispatch({ type: "navigate", reason: "history" });
    };
    window.addEventListener("popstate", history);
    return () => window.removeEventListener("popstate", history);
  }, []);

  const getAnalytics = useCallback(() => {
    const props = current.current;
    if (!readyRef.current) return;
    if (journal.current?.scope !== props.scope && journal.current?.eventId === props.facts.event_id && (journal.current.scope.includes(":visitor:") || journal.current.scope.endsWith(":anonymous"))) {
      journal.current.analytics.move(props.scope);
      journal.current.scope = props.scope;
    }
    if (journal.current?.scope !== props.scope) {
      let storage: Storage | undefined;
      try { if (props.persistAnalytics !== false) storage = window.sessionStorage; } catch { /* Ordinary editing remains usable. */ }
      journal.current = { scope: props.scope, eventId: props.facts.event_id, analytics: new ParticipationAnalytics(props.scope, { ...props.facts, is_test_run: props.facts.is_test_run ?? window.ENV?.SENTRY_ENVIRONMENT === "development" }, trackParticipation, storage, undefined, stepRef.current === "tickets") };
    }
    for (const action of queuedActions.current) {
      if (action.journal === journal.current.analytics) action.finish();
    }
    queuedActions.current = [];
    journal.current.analytics.update({ ...props.facts, is_test_run: props.facts.is_test_run ?? window.ENV?.SENTRY_ENVIRONMENT === "development" });
    return journal.current.analytics;
  }, []);

  useEffect(() => {
    dispatch({ type: "scope", scope: props.scope });
    // Confirmation routes have no ?step. Restore their journal only when an
    // authoritative outcome arrives; simply viewing a plan is not a new flow.
    const queued = queuedOutcomes.current.scope === props.scope && queuedOutcomes.current.values.length > 0;
    if (!step && !queued) return;
    const analytics = getAnalytics();
    if (!analytics) return;
    if (step) analytics.view(step, stateRef.current.navigationReason);
    if (queuedOutcomes.current.scope === props.scope) {
      for (const outcome of queuedOutcomes.current.values) analytics.complete(outcome);
      queuedOutcomes.current.values = [];
    }
  }, [props.scope, props.facts, props.persistAnalytics, step, identityReady, getAnalytics]);

  useEffect(() => {
    // The URL stays unchanged while route loaders run. A new callback on each
    // render must not restart that navigation and abort its pending requests.
    if (step && step !== props.requestedStep) current.current.onNavigate(step, true);
  }, [step, props.requestedStep]);

  const go = useCallback((target: ParticipationStep, reason?: NavigationReason) => {
    const { policy, onNavigate } = current.current;
    const from = policy.steps.findIndex(value => value.id === stepRef.current);
    const to = policy.steps.findIndex(value => value.id === target);
    const direction = reason ?? (to < from ? "back" : "next");
    if (stateRef.current.pending?.blocksNavigation && direction !== "next") return;
    stateRef.current = participationReducer(stateRef.current, { type: "navigate", reason: direction });
    dispatch({ type: "navigate", reason: direction });
    onNavigate(resolveParticipationStep(target, policy) ?? target, direction === "recovery");
  }, []);
  const adjacent = useCallback((direction: -1 | 1) => {
    if (!stepRef.current) return;
    const target = adjacentParticipationStep(stepRef.current, current.current.policy, direction);
    if (target) go(target, direction === -1 ? "back" : "next");
  }, [go]);
  const back = useCallback(() => adjacent(-1), [adjacent]);
  const next = useCallback(() => adjacent(1), [adjacent]);
  const close = useCallback(() => {
    if (stateRef.current.pending?.blocksNavigation) return;
    journal.current?.analytics.close();
    current.current.onClose();
  }, []);
  const run = useCallback(async <T,>(operation: ParticipationOperation, work: () => Promise<T>): Promise<T | undefined> => {
    if (stateRef.current.pending) return;
    const scope = current.current.scope;
    const actionJournal = readyRef.current && journal.current?.scope === scope ? journal.current.analytics : undefined;
    const action = actionJournal?.start(operation);
    const finish = (result: Parameters<NonNullable<typeof action>["finish"]>[0], error?: Parameters<NonNullable<typeof action>["finish"]>[1]) => {
      if (!action || !actionJournal) return;
      const emit = () => action.finish(result, error);
      if (readyRef.current && journal.current?.scope === current.current.scope && journal.current.analytics === actionJournal) emit();
      else queuedActions.current.push({ journal: actionJournal, finish: emit });
    };
    const pending = { id: action?.id ?? crypto.randomUUID(), operation, blocksNavigation: operation !== "quote" && operation !== "check_outcome" };
    stateRef.current = participationReducer(stateRef.current, { type: "started", pending });
    dispatch({ type: "started", pending });
    try {
      const result = await work();
      const failed = result === false || result && typeof result === "object" && "ok" in result && result.ok === false;
      finish(failed ? "failure" : result === undefined ? "unknown" : "success", failed ? "unknown" : undefined);
      if (current.current.scope === scope || journal.current?.analytics === actionJournal) return result;
    } catch (error) {
      finish(operation === "claim" || operation === "prepare_payment" || operation === "confirm_payment" ? "unknown" : "failure", "network");
      throw error;
    } finally {
      if (current.current.scope === scope) {
        stateRef.current = participationReducer(stateRef.current, { type: "finished", id: pending.id });
        dispatch({ type: "finished", id: pending.id });
      }
    }
  }, []);
  const complete = useCallback((outcome: ParticipationOutcome) => {
    const scope = current.current.scope;
    const analytics = getAnalytics();
    if (analytics) analytics.complete(outcome);
    else {
      if (queuedOutcomes.current.scope !== scope) queuedOutcomes.current = { scope, values: [] };
      queuedOutcomes.current.values.push(outcome);
    }
  }, [getAnalytics]);
  const observe = useCallback((operation: ParticipationOperation) => readyRef.current && journal.current?.scope === current.current.scope ? journal.current.analytics.start(operation) : undefined, []);
  return { step, pending, go, back, next, close, run, complete, observe };
}

export function ParticipationFlowBoundary({ flow, children }: { flow: ParticipationFlow; children: ReactNode }) {
  return <Context.Provider value={flow}>{children}</Context.Provider>;
}

export function ParticipationFlowProvider(props: Props) {
  const flow = useParticipationController(props);
  return <ParticipationFlowBoundary flow={flow}>{props.children}</ParticipationFlowBoundary>;
}
